export interface Generational {
  generation: number
  stale: boolean
}

/** A write the rows now reflect: whatever is in flight is out of date. */
export const mutated = (state: Generational): number => state.generation + 1

/** Whether a response's generation predates a write the store has applied. */
export const outdated = (state: Generational, generation?: number): boolean =>
  generation !== undefined && generation !== state.generation

/**
 * Runs `load` once, and once more if the store's generation moved while it
 * ran — the reducer dropped that response and left the store stale.
 */
export const loadCurrent = async <R>(
  readGeneration: () => number,
  load: (generation: number) => Promise<R>,
): Promise<R> => {
  const generation = readGeneration()
  const result = await load(generation)
  return readGeneration() !== generation ? load(readGeneration()) : result
}

// ── What every grid store does with a write ─────────────────────────────

/** A row a grid store keeps: one id, one status, and whether it is an amendment standing in for a record. */
export interface ScreenRow {
  id: string
  status: string
  amendment: boolean
}

/** A grid store: its rows, and the generation they are at. */
export interface ScreenState<R> extends Generational {
  rows: R[]
}

/** The ids a per-id command response says the ledger accepted. */
export const acceptedIds = (payload: { id?: string; error?: unknown }[] | undefined): string[] =>
  (Array.isArray(payload) ? payload : [])
    .filter(result => result?.id && !result.error)
    .map(r => r.id as string)

/** The one id a single-record command response says the ledger accepted, or none. */
export const oneId = (action: { payload?: { id?: string; error?: unknown } }): string[] =>
  action.payload?.error || !action.payload?.id ? [] : [action.payload.id]

/**
 * A status flip changes nothing else on the row — the ledger archives one
 * contract and creates its counterpart with the same fields. `amendment` picks
 * which of the rows sharing an id the command was about: a record's command
 * never touches the amendment standing in for it, and vice versa.
 */
export const withStatus =
  <R extends ScreenRow>(ids: Iterable<string | undefined>, status: R['status'], amendment: boolean) =>
  (rows: R[]): R[] => {
    const flipped = new Set([...ids].filter(Boolean))
    if (flipped.size === 0) return rows
    // Always a NEW array, even when no row matched: the row may be in a read
    // still in flight (a submit during the first load), and withRows must move
    // the generation so that read is dropped and taken again — otherwise its
    // pre-write rows would land as current and undo the submit.
    return rows.map(row =>
      row.amendment === amendment && flipped.has(row.id) ? { ...row, status } : row,
    )
  }

/** A write the rows could not be patched for: the next load brings it. */
export const invalidate = <S extends Generational>(state: S): S => ({
  ...state,
  stale: true,
  generation: mutated(state),
})

/** The rows after a write was applied to them: whatever read is in flight is out of date. */
export const withRows = <R, S extends ScreenState<R>>(state: S, rows: R[]): S =>
  rows === state.rows ? state : { ...state, rows, generation: mutated(state) }

/**
 * What a terminate command answers (POST /approved-deals/terminate and
 * /approved-facilities/terminate): `{ id, amendmentId, isSuccess, error }` —
 * the record's id and the Termination amendment's own, which the row menu
 * opens and deletes the amendment by. Ids, not a record: the thunks dispatch
 * it as DEAL_TERMINATED / FACILITY_TERMINATED and the amendment stores take
 * nothing from it. Undefined when there is nothing to apply.
 */
export const terminationAnswer = (action: {
  payload?: any
}): { id: string; amendmentId?: string } | undefined => {
  const answer = action.payload
  return answer?.id && answer?.isSuccess !== false && !answer?.error
    ? { id: answer.id, amendmentId: answer.amendmentId }
    : undefined
}

/**
 * Terminated from the row menu: the record's row shows as the termination
 * (`asTermination` builds it for the grid); an answer with nothing to apply
 * leaves the grid stale, as every unpatchable write does.
 */
export const withTermination = <R extends ScreenRow, S extends ScreenState<R>>(
  state: S,
  action: { payload?: any },
  asTermination: (row: R, amendmentId?: string) => R,
): S => {
  const terminated = terminationAnswer(action)
  if (!terminated) return invalidate(state)
  return withRows(
    state,
    state.rows.map(row =>
      row.id === terminated.id ? asTermination(row, terminated.amendmentId) : row,
    ),
  )
}

